import React from "react";
import { fetchWithSupabaseAuth } from "../lib/supabaseClient.js";
import { reportObservedDeploymentVersion } from "../lib/deploymentUpdateRuntime.js";
import { readDispatchDateFromSearch } from "../utils/dispatchDateUrls.js";
import { isDiagnosticsActive, recordDiagnosticsEvent } from "./useDiagnostics.js";

const shellDataCache = new Map();
const inFlightShellDataCache = new Map();
const shellDataInvalidationListeners = new Set();

export function getShellDataDiagnosticsSnapshot() {
  return {
    cacheKeyCount: shellDataCache.size,
    cacheKeys: [...shellDataCache.keys()],
    inFlightKeys: [...inFlightShellDataCache.keys()],
    inFlightRequestCount: inFlightShellDataCache.size,
  };
}

// PHASE 1.5 TEMP: diagnostic local des cardinalites cache/in-flight shell.
function logPhase15ShellDataSnapshot(event, details = {}) {
  if (!isDiagnosticsActive()) {
    return;
  }

  recordDiagnosticsEvent("useShellData", event, {
    ...details,
    ...getShellDataDiagnosticsSnapshot(),
    shellDataCacheSize: shellDataCache.size,
    inFlightShellDataCacheSize: inFlightShellDataCache.size,
  }, {
    countAsReset: event === "cache_invalidate",
  });
}

function normalizeShellPathname(pathname) {
  if (typeof pathname !== "string" || !pathname.trim()) {
    return "/";
  }

  return pathname.startsWith("/") ? pathname : `/${pathname}`;
}

function normalizeShellAssetVersion(value) {
  if (typeof value !== "string") {
    return "";
  }

  return value.trim();
}

function normalizeDispatchDate(value) {
  return typeof value === "string" ? value.trim() : "";
}

export function buildShellBootstrapApiUrl(pathname, { dispatchDate = "" } = {}) {
  const normalizedPathname = normalizeShellPathname(pathname);
  const normalizedDispatchDate = normalizeDispatchDate(dispatchDate);
  const searchParams = new URLSearchParams({ pathname: normalizedPathname });

  if (normalizedDispatchDate) {
    searchParams.set("dispatch_date", normalizedDispatchDate);
  }

  return `/api/shell?${searchParams.toString()}`;
}

function readCachedShellData(apiUrl) {
  return typeof apiUrl === "string" && apiUrl && shellDataCache.has(apiUrl)
    ? shellDataCache.get(apiUrl)
    : null;
}

function writeCachedShellData(apiUrl, nextData) {
  if (typeof apiUrl === "string" && apiUrl) {
    shellDataCache.set(apiUrl, nextData);
    logPhase15ShellDataSnapshot("cache_write", { apiUrl });
  }

  return nextData;
}

function notifyShellDataInvalidation() {
  shellDataInvalidationListeners.forEach((listener) => {
    try {
      listener();
    } catch {}
  });
}

function subscribeToShellDataInvalidation(listener) {
  if (typeof listener !== "function") {
    return () => {};
  }

  shellDataInvalidationListeners.add(listener);
  return () => {
    shellDataInvalidationListeners.delete(listener);
  };
}

export function invalidateShellDataCache() {
  shellDataCache.clear();
  inFlightShellDataCache.clear();
  logPhase15ShellDataSnapshot("cache_invalidate");
  notifyShellDataInvalidation();
}

async function fetchShellBootstrap(apiUrl, { signal } = {}) {
  const response = await fetchWithSupabaseAuth(apiUrl, {
    cache: "no-store",
    signal,
  });
  if (!response.ok) {
    throw new Error(`Shell bootstrap failed with status ${response.status}`);
  }

  const data = await response.json();
  if (!data || typeof data !== "object") {
    return data;
  }

  const nextAssetVersion = normalizeShellAssetVersion(data.asset_version);
  reportObservedDeploymentVersion(nextAssetVersion, "api_shell");

  return writeCachedShellData(apiUrl, data);
}

export async function loadShellBootstrapData(pathname, {
  dispatchDate = "",
  enabled = true,
  force = false,
  signal,
} = {}) {
  if (enabled === false) {
    return null;
  }

  const apiUrl = buildShellBootstrapApiUrl(pathname, { dispatchDate });
  if (!force) {
    const cachedData = readCachedShellData(apiUrl);
    if (cachedData != null) {
      return cachedData;
    }
  }

  const existingRequest = inFlightShellDataCache.get(apiUrl);
  if (existingRequest) {
    logPhase15ShellDataSnapshot("request_reused", { apiUrl });
    return existingRequest;
  }

  const requestPromise = fetchShellBootstrap(apiUrl, { signal })
    .finally(() => {
      inFlightShellDataCache.delete(apiUrl);
      logPhase15ShellDataSnapshot("request_settled", { apiUrl });
    });

  inFlightShellDataCache.set(apiUrl, requestPromise);
  logPhase15ShellDataSnapshot("request_started", { apiUrl, force });
  return requestPromise;
}

export default function useShellData(pathname, { enabled = true } = {}) {
  const isEnabled = enabled !== false;
  const resolvedPathname = normalizeShellPathname(pathname);
  const resolvedDispatchDate = readDispatchDateFromSearch(
    typeof window === "undefined" ? "" : (window.location.search || ""),
  );
  const requestUrl = buildShellBootstrapApiUrl(resolvedPathname, { dispatchDate: resolvedDispatchDate });
  const [shellData, setShellData] = React.useState(() => (
    isEnabled ? readCachedShellData(requestUrl) : null
  ));
  const [reloadToken, setReloadToken] = React.useState(0);
  const previousRequestUrlRef = React.useRef(requestUrl);
  const activeRequestUrlRef = React.useRef(requestUrl);

  React.useEffect(() => {
    if (!isEnabled) {
      return undefined;
    }

    return subscribeToShellDataInvalidation(() => {
      setReloadToken((currentToken) => currentToken + 1);
    });
  }, [isEnabled]);

  React.useEffect(() => {
    if (!isEnabled) {
      activeRequestUrlRef.current = "";
      previousRequestUrlRef.current = requestUrl;
      setShellData(null);
      return undefined;
    }

    activeRequestUrlRef.current = requestUrl;

    const previousRequestUrl = previousRequestUrlRef.current;
    const requestChanged = previousRequestUrl !== requestUrl;
    previousRequestUrlRef.current = requestUrl;

    if (requestChanged) {
      setShellData(readCachedShellData(requestUrl));
    }

    const abortController = new AbortController();

    loadShellBootstrapData(resolvedPathname, {
      dispatchDate: resolvedDispatchDate,
      enabled: isEnabled,
      force: !requestChanged && reloadToken > 0,
      signal: abortController.signal,
    })
      .then((data) => {
        if (activeRequestUrlRef.current !== requestUrl) {
          return;
        }

        setShellData(data ?? null);
      })
      .catch((error) => {
        if (error?.name === "AbortError") {
          return;
        }
      });

    return () => {
      abortController.abort();
    };
  }, [isEnabled, reloadToken, requestUrl, resolvedDispatchDate, resolvedPathname]);

  return shellData;
}
